// Hand-drawn feel shared by the desktop hero split and the section seams: a slow wobble like an
// unsteady hand, plus fine grain like a dry marker catching paper. The noise is fixed per sample,
// so a drawn line keeps its shape as it moves rather than shimmering.
const WOBBLE = 0.32;
const GRAIN = 0.14;

/**
 * @param seed - Stable integer so the drawn edge is identical on every load
 * @returns A deterministic pseudo-random generator in the range [0, 1)
 */
export function makeRng(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

/**
 * @param seed - Seed for this stroke edge
 * @param amplitude - Scales the whole offset, so a looser stroke can wander further
 * @param samples - Number of segments along the stroke
 * @returns Offsets (in the caller's units) for each of the `samples + 1` points along the stroke
 */
export function strokeOffsets(seed: number, amplitude: number, samples: number): number[] {
  const rng = makeRng(seed);
  const waves = [1.3, 3.1, 7.7].map(frequency => ({ frequency, phase: rng() * Math.PI * 2 }));
  return Array.from({ length: samples + 1 }, (_, i) => {
    const t = i / samples;
    const wobble = waves.reduce((sum, wave, k) => sum + Math.sin(t * wave.frequency * Math.PI * 2 + wave.phase) / (k + 1), 0);
    const grain = (rng() - 0.5) * 2;
    return amplitude * (wobble * WOBBLE * 0.55 + grain * GRAIN);
  });
}

/**
 * @param seed - Seed for the unevenness
 * @param samples - Number of segments along the stroke
 * @returns Marker pressure (roughly 0.1–1) per point: swells through the middle and thins towards both ends
 */
export function markerPressure(seed: number, samples: number): number[] {
  const rng = makeRng(seed);
  const phase = rng() * Math.PI * 2;
  return Array.from({ length: samples + 1 }, (_, i) => {
    const t = i / samples;
    return 0.5 + 0.4 * Math.sin(Math.PI * t) + 0.1 * Math.sin(t * 5 * Math.PI + phase);
  });
}
